import { Component, OnInit, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserTable } from 'src/app/services/models/user-table.model';
import { DatasetService } from 'src/app/services/dataset.service';
import { MetricService } from 'src/app/services/metric.service';
import { UserTableService } from 'src/app/services/user-table.service';
import { Dataset } from 'src/app/services/models/datasets.models';
import { MetricDto } from 'src/app/services/models/metric.models';
import { DatasetFieldData } from './dataset-field/dataset-field-data.interface';
import { WorkspaceItem, DatasetWorkspaceItem, MetricWorkspaceItem, WorkspaceConnection } from './models/workspace-item.interface';
import { TableColumnDef } from 'src/app/services/models/user-table.model';
import { Store, select } from '@ngrx/store';
import { selectCurrentUserEmail } from 'src/app/store/app-state.selectors';
import { take } from 'rxjs/operators';

@Component({
  selector: 'bhub-table-editor',
  templateUrl: './table-editor.component.html',
  styleUrls: ['./table-editor.component.scss'],
  standalone: false
})
export class TableEditorComponent implements OnInit, AfterViewInit {
  @Input() table?: UserTable; // If provided, we're editing an existing table
  @Output() save = new EventEmitter<UserTable>();
  @Output() cancel = new EventEmitter<void>();

  tableForm: FormGroup;
  isEditMode = false;
  sharedToInput = '';

  // Data for table creation
  datasets: Dataset[] = [];
  metrics: MetricDto[] = [];
  loadingData = true;

  // Visual builder state
  selectedDataset?: Dataset;
  selectedMetric?: MetricDto;
  workspaceTables: Dataset[] = [];

    // Workspace positioning
  workspaceTablesWithPosition: Array<{dataset: Dataset, x: number, y: number, zIndex: number}> = [];
  workspaceMetricsWithPosition: Array<{metric: MetricDto, x: number, y: number, zIndex: number}> = [];

  // Z-index management
  private currentZIndex = 1;

  // Table connections
  tableConnections: Array<{
    fromTable: number,
    fromField: string,
    toTable: number,
    toField: string,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number
  }> = [];

  // Field positioning data
  fieldPositions: Map<string, {x: number, y: number, width: number, height: number}> = new Map();

  // Selected fields for the table
  selectedFields: Set<string> = new Set(); // Format: "tableIndex_fieldName"

       // Table column definitions
  tableColumnDefinitions: TableColumnDef[] = [];

         // Column reordering state
  // (removed drag and drop state - using arrow buttons instead)

  // Save state
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private datasetService: DatasetService,
    private metricService: MetricService,
    private userTableService: UserTableService,
    private store: Store
  ) {
    this.tableForm = this.fb.group({
      tableName: ['', [Validators.required, Validators.minLength(1)]],
      tableDescription: [''],
      isShared: [false],
      isDefault: [false],
      sharedTo: [[]]
    });
  }

    ngOnInit() {
    this.isEditMode = !!this.table;

    if (this.table) {
      // Edit mode - populate form with existing table data
      this.tableForm.patchValue({
        tableName: this.table.tableName,
        tableDescription: this.table.tableDescription || '',
        isShared: this.table.isShared,
        isDefault: this.table.isDefault,
        sharedTo: this.table.sharedTo || []
      });
      this.sharedToInput = this.table.sharedTo?.join(', ') || '';

      // Store the table column definitions for restoration after data loads
      this.tableColumnDefinitions = [...this.table.columnList];
    }

    // Load datasets and metrics
    this.loadData();
  }

  ngAfterViewInit() {
    // Calculate field positions after view is initialized
    if (this.workspaceTablesWithPosition.length > 0) {
      this.calculateFieldPositions();
    }
  }

  // Method to recalculate field positions when needed
  recalculateFieldPositions() {
    if (this.workspaceTablesWithPosition.length > 0) {
      this.calculateFieldPositions();
    }
  }



  loadData() {
    this.loadingData = true;

    // Load datasets (excluding Reference type as requested)
    this.datasetService.getDatasetReferences().subscribe({
      next: (datasetMap) => {
        this.datasets = Array.from(datasetMap.values());
        this.checkDataLoaded();
      },
      error: (error) => {
        this.checkDataLoaded();
      }
    });

    // Load metrics
    this.metricService.getMetricList().subscribe({
      next: (metricMap) => {
        this.metrics = Array.from(metricMap.values());
        this.checkDataLoaded();
      },
      error: (error) => {
        this.checkDataLoaded();
      }
    });
  }

  checkDataLoaded() {
    // Both datasets and metrics have been attempted to load
    this.loadingData = false;

    // If we're in edit mode and data is now loaded, restore the workspace state
    if (this.isEditMode && this.table && this.tableColumnDefinitions.length > 0) {
      this.restoreWorkspaceFromTableDefinition();
    }
  }

  onDatasetSelect(event: any) {
    const dataset = event.value;
    this.selectedDataset = dataset;
    if (dataset) {
      this.addDatasetToWorkspace(dataset);
    }
  }

  onMetricSelect(event: any) {
    const metric = event.value;
    this.selectedMetric = metric;
    if (metric) {
      this.addMetricToWorkspace(metric);
    }
  }

  addDatasetToWorkspace(dataset: Dataset) {
    // Check if dataset is already in workspace
    const exists = this.workspaceTables.find(d => d.datasetReferenceTable.datasetId === dataset.datasetReferenceTable.datasetId);
    if (!exists) {
      this.workspaceTables.push(dataset);
            // Add to positioned tables with default position
      const newPosition = this.calculateNewTablePosition();
      this.workspaceTablesWithPosition.push({
        dataset: dataset,
        x: newPosition.x,
        y: newPosition.y,
        zIndex: this.getNextZIndex()
      });

      // Calculate field positions and detect connections
      this.calculateFieldPositions();
      this.detectConnections();
    }
  }

  addMetricToWorkspace(metric: MetricDto) {
    // Check if metric is already in workspace
    const exists = this.workspaceMetricsWithPosition.find(m => m.metric.metricId === metric.metricId);
    if (!exists) {
      // Add metric to workspace with position
      const newPosition = this.calculateNewMetricPosition();
      this.workspaceMetricsWithPosition.push({
        metric: metric,
        x: newPosition.x,
        y: newPosition.y,
        zIndex: this.getNextZIndex()
      });

      // Add related datasets to workspace
      this.addRelatedDatasetsForMetric(metric);

      // Calculate field positions and detect connections
      this.calculateFieldPositions();
      this.detectConnections();
    }
  }

    addRelatedDatasetsForMetric(metric: MetricDto) {
    // Parse metric operations to find datasets and fields
    const datasetIds = new Set<number>();

    metric.operations.forEach(operation => {
      if (operation.dependencyType === "Dataset Field") {
        // Parse dependencyValue which is in format "datasetId_fieldId"
        const parts = operation.dependencyValue.split('_');
        if (parts.length === 2) {
          const datasetId = parseInt(parts[0]);
          const fieldId = parseInt(parts[1]);

          if (!isNaN(datasetId)) {
            datasetIds.add(datasetId);
          }
        }
      }
    });

    // Find datasets that match the datasetIds from the metric operations
    const relatedDatasets = this.datasets.filter(dataset =>
      datasetIds.has(dataset.datasetReferenceTable.datasetId)
    );

    // Add related datasets to workspace
    relatedDatasets.forEach(dataset => {
      this.addDatasetToWorkspace(dataset);
    });
  }

  calculateNewMetricPosition() {
    // Calculate position for new metric (different from table positioning)
    const baseX = 100;
    const baseY = 600; // Place metrics below tables
    const offsetX = 400;
    const offsetY = 200;

    const metricCount = this.workspaceMetricsWithPosition.length;
    const row = Math.floor(metricCount / 3);
    const col = metricCount % 3;

    return {
      x: baseX + (col * offsetX),
      y: baseY + (row * offsetY)
    };
  }

    calculateNewTablePosition() {
    // Calculate position for new table (staggered layout in larger workspace)
    const baseX = 100;
    const baseY = 100;
    const offsetX = 350;
    const offsetY = 250;

    const tableCount = this.workspaceTablesWithPosition.length;
    const row = Math.floor(tableCount / 4);
    const col = tableCount % 4;

    return {
      x: baseX + (col * offsetX),
      y: baseY + (row * offsetY)
    };
  }

  removeDatasetFromWorkspace(dataset: Dataset) {
    const index = this.workspaceTables.findIndex(d => d.datasetReferenceTable.datasetId === dataset.datasetReferenceTable.datasetId);
    if (index !== -1) {
      this.workspaceTables.splice(index, 1);
    }

    // Remove from positioned tables
    const posIndex = this.workspaceTablesWithPosition.findIndex(t => t.dataset.datasetReferenceTable.datasetId === dataset.datasetReferenceTable.datasetId);
    if (posIndex !== -1) {
      this.workspaceTablesWithPosition.splice(posIndex, 1);

      // Remove any connections that reference this table
      this.tableConnections = this.tableConnections.filter(connection =>
        connection.fromTable !== posIndex && connection.toTable !== posIndex
      );

      // Recalculate field positions after table removal
      this.calculateFieldPositions();
    }
  }

  getDatasetColumns(dataset: Dataset) {
    return dataset.dataTableSchemas || [];
  }

  private getFieldById(datasetId: number, fieldId: number): any {
    const dataset = this.datasets.find(d => d.datasetReferenceTable.datasetId === datasetId);
    if (dataset) {
      return dataset.dataTableSchemas?.find(field => field.fieldId === fieldId);
    }
    return null;
  }

    private determineConnectionType(tableIndex: number, fieldName: string): 'primary' | 'secondary' {
    // Check if this field is directly connected to a metric
    const isDirectlyConnectedToMetric = this.tableConnections.some(conn =>
      conn.toTable === tableIndex &&
      conn.toField === fieldName &&
      conn.fromTable === -1 // Metric connection
    );

    if (isDirectlyConnectedToMetric) {
      return 'primary'; // Directly connected to metric
    }

    // Check if this field is in the same dataset as a field that's directly connected to a metric
    const tableData = this.workspaceTablesWithPosition[tableIndex];
    if (tableData) {
      const columns = this.getDatasetColumns(tableData.dataset);

      // Check if any field in this same dataset is directly connected to a metric
      const hasDirectMetricConnection = columns.some(column =>
        this.tableConnections.some(conn =>
          conn.toTable === tableIndex &&
          conn.toField === column.fieldName &&
          conn.fromTable === -1 // Metric connection
        )
      );

      if (hasDirectMetricConnection) {
        return 'primary'; // In same dataset as a field directly connected to metric
      }
    }

    // Check if this field is connected to a field that is directly connected to a metric
    const incomingConnections = this.tableConnections.filter(conn =>
      conn.toTable === tableIndex &&
      conn.toField === fieldName &&
      conn.fromTable !== -1 // Not a metric connection
    );

    for (const incomingConn of incomingConnections) {
      // Check if the source field is directly connected to a metric
      const sourceFieldIsDirectlyConnectedToMetric = this.tableConnections.some(conn =>
        conn.toTable === incomingConn.fromTable &&
        conn.toField === incomingConn.fromField &&
        conn.fromTable === -1 // Metric connection
      );

      if (sourceFieldIsDirectlyConnectedToMetric) {
        return 'secondary'; // Connected to a primary field
      }
    }

    return 'secondary'; // Default fallback
  }


  detectConnections() {
    this.tableConnections = [];

    // Detect table-to-table connections
    for (let i = 0; i < this.workspaceTablesWithPosition.length; i++) {
      const fromTable = this.workspaceTablesWithPosition[i];
      const fromColumns = this.getDatasetColumns(fromTable.dataset);

      for (let j = 0; j < this.workspaceTablesWithPosition.length; j++) {
        if (i === j) continue;

        const toTable = this.workspaceTablesWithPosition[j];
        const toColumns = this.getDatasetColumns(toTable.dataset);

        // Look for foreign key relationships using referenceDataId and referenceFieldId
        for (const fromColumn of fromColumns) {
          if (fromColumn.referenceDataId && fromColumn.referenceDataId > 0 &&
              fromColumn.referenceFieldId && fromColumn.referenceFieldId > 0) {

            // Check if this field references the target table
            if (fromColumn.referenceDataId === toTable.dataset.datasetReferenceTable.datasetId) {
              // Find the specific field being referenced in the target table
              const referencedField = toColumns.find(col => col.fieldId === fromColumn.referenceFieldId);

              if (referencedField) {
                this.createConnection(i, fromColumn.fieldName, j, referencedField.fieldName);
              } else {
              }
            }
          }
        }
      }
    }

    // Detect metric-to-field connections
    this.detectMetricConnections();
  }

    detectMetricConnections() {
    // For each metric, create connections to the relevant fields in datasets
    this.workspaceMetricsWithPosition.forEach((metricData, metricIndex) => {
      const metric = metricData.metric;

      // Parse metric operations to find specific dataset and field connections
      metric.operations.forEach(operation => {
        if (operation.dependencyType === "Dataset Field") {
          // Parse dependencyValue which is in format "datasetId_fieldId"
          const parts = operation.dependencyValue.split('_');
          if (parts.length === 2) {
            const datasetId = parseInt(parts[0]);
            const fieldId = parseInt(parts[1]);

            if (!isNaN(datasetId) && !isNaN(fieldId)) {
              // Find the dataset in workspace
              const tableData = this.workspaceTablesWithPosition.find(tableData =>
                tableData.dataset.datasetReferenceTable.datasetId === datasetId
              );

              if (tableData) {
                const tableIndex = this.workspaceTablesWithPosition.indexOf(tableData);
                const columns = this.getDatasetColumns(tableData.dataset);

                // Find the specific field by fieldId
                const targetField = columns.find(col => col.fieldId === fieldId);

                if (targetField) {
                  this.createMetricConnection(metricIndex, tableIndex, targetField.fieldName);
                } else {
                  console.warn(`Field with ID ${fieldId} not found in dataset ${tableData.dataset.datasetReferenceTable.datasetName}`);
                }
              } else {
                console.warn(`Dataset with ID ${datasetId} not found in workspace for metric ${metric.metricName}`);
              }
            }
          }
        }
      });
    });
  }



  getFieldPosition(tableIndex: number, fieldName: string) {
    const fieldKey = `${tableIndex}_${fieldName}`;
    return this.fieldPositions.get(fieldKey);
  }

  createConnection(fromTableIndex: number, fromField: string, toTableIndex: number, toField: string) {
    const fromTable = this.workspaceTablesWithPosition[fromTableIndex];
    const toTable = this.workspaceTablesWithPosition[toTableIndex];

    // Get precise field positions
    const fromFieldKey = `${fromTableIndex}_${fromField}`;
    const toFieldKey = `${toTableIndex}_${toField}`;

    const fromFieldPos = this.fieldPositions.get(fromFieldKey);
    const toFieldPos = this.fieldPositions.get(toFieldKey);

    let fromX, fromY, toX, toY;

    if (fromFieldPos && toFieldPos) {
      // Use precise field positions
      fromX = fromFieldPos.x + fromFieldPos.width; // Right edge of from field
      fromY = fromFieldPos.y + (fromFieldPos.height / 2); // Center of from field
      toX = toFieldPos.x - 5; // Just before left edge of to field
      toY = toFieldPos.y + (toFieldPos.height / 2); // Center of to field
    } else {
      // Fallback to table-based positioning
      fromX = fromTable.x + 250;
      fromY = fromTable.y + 80;
      toX = toTable.x;
      toY = toTable.y + 80;
    }

    this.tableConnections.push({
      fromTable: fromTableIndex,
      fromField: fromField,
      toTable: toTableIndex,
      toField: toField,
      fromX: fromX,
      fromY: fromY,
      toX: toX,
      toY: toY
    });
  }

  createMetricConnection(metricIndex: number, tableIndex: number, fieldName: string) {
    const metricData = this.workspaceMetricsWithPosition[metricIndex];
    const tableData = this.workspaceTablesWithPosition[tableIndex];

    // Get metric position (right side of metric box)
    const metricX = metricData.x + 250; // Right edge of metric box (assuming 250px width)
    const metricY = metricData.y + 25; // Center of metric box (assuming 50px height)

    // Get field position
    const fieldKey = `${tableIndex}_${fieldName}`;
    const fieldPos = this.fieldPositions.get(fieldKey);

    let toX, toY;

    if (fieldPos) {
      // Use precise field positions
      toX = fieldPos.x - 5; // Just before left edge of field
      toY = fieldPos.y + (fieldPos.height / 2); // Center of field
    } else {
      // Fallback to table-based positioning
      toX = tableData.x;
      toY = tableData.y + 80;
    }

    this.tableConnections.push({
      fromTable: -1, // -1 indicates this is a metric connection
      fromField: `metric_${metricIndex}`,
      toTable: tableIndex,
      toField: fieldName,
      fromX: metricX,
      fromY: metricY,
      toX: toX,
      toY: toY
    });
  }

    updateConnectionPositions() {
    // Update all connection positions based on current table positions and field positions
    this.tableConnections.forEach(connection => {
      // Handle metric connections (fromTable = -1 indicates metric connection)
      if (connection.fromTable === -1) {
        // This is a metric connection
        const metricIndex = parseInt(connection.fromField.replace('metric_', ''));
        const metricData = this.workspaceMetricsWithPosition[metricIndex];
        const toTable = this.workspaceTablesWithPosition[connection.toTable];

                 if (metricData && toTable) {
           // Get metric position (right side of metric box)
           const metricX = metricData.x + 250; // Right edge of metric box (assuming 250px width)
           const metricY = metricData.y + 25; // Center of metric box (assuming 50px height)

          // Get field position
          const toFieldKey = `${connection.toTable}_${connection.toField}`;
          const toFieldPos = this.fieldPositions.get(toFieldKey);

          if (toFieldPos) {
            // Use precise field positions
            connection.fromX = metricX;
            connection.fromY = metricY;
            connection.toX = toFieldPos.x - 5; // Just before left edge of field
            connection.toY = toFieldPos.y + (toFieldPos.height / 2); // Center of field
          } else {
            // Fallback to table-based positioning
            connection.fromX = metricX;
            connection.fromY = metricY;
            connection.toX = toTable.x;
            connection.toY = toTable.y + 80;
          }
        }
      } else {
        // Handle table-to-table connections
        const fromTable = this.workspaceTablesWithPosition[connection.fromTable];
        const toTable = this.workspaceTablesWithPosition[connection.toTable];

        if (fromTable && toTable) {
          // Get field positions
          const fromFieldKey = `${connection.fromTable}_${connection.fromField}`;
          const toFieldKey = `${connection.toTable}_${connection.toField}`;

          const fromFieldPos = this.fieldPositions.get(fromFieldKey);
          const toFieldPos = this.fieldPositions.get(toFieldKey);

          if (fromFieldPos && toFieldPos) {
            // Use precise field positions
            connection.fromX = fromFieldPos.x + fromFieldPos.width; // Right edge of from field
            connection.fromY = fromFieldPos.y + (fromFieldPos.height / 2); // Center of from field
            connection.toX = toFieldPos.x - 5; // Just before left edge of to field
            connection.toY = toFieldPos.y + (toFieldPos.height / 2); // Center of to field
          } else {
            // Fallback to table-based positioning
            connection.fromX = fromTable.x + 250;
            connection.fromY = fromTable.y + 80;
            connection.toX = toTable.x;
            connection.toY = toTable.y + 80;
          }
        }
      }
    });
  }

  calculateFieldPositions() {
    this.fieldPositions.clear();

    // Use setTimeout to ensure DOM elements are rendered
    setTimeout(() => {
      this.workspaceTablesWithPosition.forEach((tableData, tableIndex) => {
        const columns = this.getDatasetColumns(tableData.dataset);

        columns.forEach((column, columnIndex) => {
          const fieldKey = `${tableIndex}_${column.fieldName}`;

          // Try to get the actual field element from the DOM
          const fieldElement = document.querySelector(`[data-table-index="${tableIndex}"][data-field-name="${column.fieldName}"]`);

          if (fieldElement) {
            const rect = fieldElement.getBoundingClientRect();
            const workspaceContainer = document.querySelector('.workspace-container');

            if (workspaceContainer) {
              const containerRect = workspaceContainer.getBoundingClientRect();
              const scrollLeft = workspaceContainer.scrollLeft;
              const scrollTop = workspaceContainer.scrollTop;

              this.fieldPositions.set(fieldKey, {
                x: rect.left - containerRect.left + scrollLeft,
                y: rect.top - containerRect.top + scrollTop,
                width: rect.width,
                height: rect.height
              });
            }
          } else {
            // Fallback to calculated positioning
            const tableX = tableData.x;
            const tableY = tableData.y;
            const headerHeight = 50;
            const columnHeaderHeight = 30;
            const rowHeight = 25;
            const fieldY = tableY + headerHeight + columnHeaderHeight + (columnIndex * rowHeight);

            this.fieldPositions.set(fieldKey, {
              x: tableX + 10,
              y: fieldY,
              width: 240,
              height: rowHeight
            });
          }
        });
      });

      // Update connection positions after field positions are calculated
      this.updateConnectionPositions();
    }, 100);
  }

  onTableDragStart(event: DragEvent, tableIndex: number) {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', tableIndex.toString());
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onTableDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onTableDrop(event: DragEvent) {
    event.preventDefault();
    const data = event.dataTransfer!.getData('text/plain');
    const workspaceContainer = (event.target as HTMLElement).closest('.workspace-container');

    if (workspaceContainer) {
      const rect = workspaceContainer.getBoundingClientRect();
      const scrollLeft = workspaceContainer.scrollLeft;
      const scrollTop = workspaceContainer.scrollTop;

      const x = event.clientX - rect.left + scrollLeft;
      const y = event.clientY - rect.top + scrollTop;

             // Check if this is a table or metric drop
       if (data.startsWith('metric_')) {
         // Handle metric drop
         const metricIndex = parseInt(data.replace('metric_', ''));
         if (metricIndex >= 0 && metricIndex < this.workspaceMetricsWithPosition.length) {
           this.workspaceMetricsWithPosition[metricIndex].x = x;
           this.workspaceMetricsWithPosition[metricIndex].y = y;

           // Bring to front
           this.bringToFront(undefined, metricIndex);

           // Recalculate field positions and update connections
           this.calculateFieldPositions();
         }
       } else {
         // Handle table drop
         const tableIndex = parseInt(data);
         if (tableIndex >= 0 && tableIndex < this.workspaceTablesWithPosition.length) {
           this.workspaceTablesWithPosition[tableIndex].x = x;
           this.workspaceTablesWithPosition[tableIndex].y = y;

           // Bring to front
           this.bringToFront(tableIndex);

           // Recalculate field positions and update connections
           this.calculateFieldPositions();
         }
       }
    }
  }

  onSharedToInputChange(event: any) {
    const value = event.target.value.trim();
    if (value) {
      const emails = value.split(',').map((email: string) => email.trim());
      this.tableForm.patchValue({ sharedTo: emails });
    } else {
      this.tableForm.patchValue({ sharedTo: [] });
    }
  }

  onSave() {
    if (this.tableForm.valid && !this.isSaving) {
      this.isSaving = true;
      const formValue = this.tableForm.value;

      // Process sharedTo input
      let sharedTo: string[] = [];
      if (this.sharedToInput.trim()) {
        sharedTo = this.sharedToInput.split(',').map((email: string) => email.trim());
      }

             // Convert TableColumnDef to the format expected by the service
       const columnList = this.tableColumnDefinitions.map(def => ({
         columnId: def.columnId,
         ordinal: def.ordinal,
         columnType: def.columnType,
         typeId: def.typeId,
         format: def.format,
         unit: def.unit,
         columnName: def.columnName,
         connectionType: def.connectionType,
         relatedFields: def.relatedFields
       }));

      // Get current user email from store
      this.store.pipe(select(selectCurrentUserEmail), take(1)).subscribe(userEmail => {
        const tableData: Omit<UserTable, 'tableId' | 'createdDate' | 'modifiedDate'> = {
          tableName: formValue.tableName,
          tableDescription: formValue.tableDescription,
          isShared: formValue.isShared,
          isDefault: formValue.isDefault,
          sharedTo: sharedTo,
          columnList: columnList,
          createdBy: userEmail || 'unknown',
          modifiedBy: userEmail || 'unknown'
        };

        // Emit the table data to parent component for API handling
        this.isSaving = false;
        this.save.emit(tableData as UserTable);
      });
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  getFieldData(tableIndex: number, fieldName: string, column: any): DatasetFieldData {
    if (fieldName === 'header') {
      return {
        x: 0,
        y: 0,
        width: 240,
        height: 30,
        fieldName: 'Field Name',
        dataType: 'Data Type',
        datasetId: 0,
        fieldId: 0,
        referenceDataId: 0,
        referenceFieldId: null
      };
    }

    const fieldKey = `${tableIndex}_${fieldName}`;
    const fieldPos = this.fieldPositions.get(fieldKey);

    if (fieldPos) {
      return {
        x: fieldPos.x,
        y: fieldPos.y,
        width: fieldPos.width,
        height: fieldPos.height,
        fieldName: column.fieldName,
        dataType: column.dataType,
        datasetId: column.datasetId,
        fieldId: column.fieldId,
        referenceDataId: column.referenceDataId || 0,
        referenceFieldId: column.referenceFieldId || 0
      };
    }

    // Fallback if position not found
    return {
      x: 0,
      y: 0,
      width: 240,
      height: 25,
      fieldName: column.fieldName,
      dataType: column.dataType,
      datasetId: column.datasetId,
      fieldId: column.fieldId,
      referenceDataId: column.referenceDataId || 0,
      referenceFieldId: column.referenceFieldId || 0
    };
  }

    get isFormValid(): boolean {
    return this.tableForm.valid && this.tableColumnDefinitions.length > 0;
  }

  // Field selection methods
  onFieldClick(tableIndex: number, fieldName: string) {
    const fieldKey = `${tableIndex}_${fieldName}`;

    if (this.selectedFields.has(fieldKey)) {
      // Deselect the field from all related datasets
      this.deselectFieldFromAllDatasets(tableIndex, fieldName);
    } else {
      // Check if this field can be selected (has connections or is from a table with selected fields)
      if (this.canSelectField(tableIndex, fieldName)) {
        // Select the field from all related datasets
        this.selectFieldFromAllDatasets(tableIndex, fieldName);
      } else {
        console.warn(`Cannot select field ${fieldName} - no valid connections found`);
        // You could show a user-friendly message here
      }
    }
  }

  canSelectField(tableIndex: number, fieldName: string): boolean {
    // For now, allow any field to be selected without restrictions
    return true;
  }

  isFieldSelected(tableIndex: number, fieldName: string): boolean {
    const fieldKey = `${tableIndex}_${fieldName}`;
    return this.selectedFields.has(fieldKey);
  }

  getFieldConnectionType(tableIndex: number, fieldName: string): 'primary' | 'secondary' | 'base' | 'none' {
    if (!this.isFieldSelected(tableIndex, fieldName)) {
      return 'none';
    }

    // Find the column definition for this field
    const tableData = this.workspaceTablesWithPosition[tableIndex];
    if (tableData) {
      const columns = this.getDatasetColumns(tableData.dataset);
      const fieldData = columns.find(col => col.fieldName === fieldName);
      if (fieldData) {
        const columnDef = this.tableColumnDefinitions.find(def => {
          if (def.columnType !== 'field') return false;
          return def.typeId === `field_${fieldData.datasetId}_${fieldData.fieldId}`;
        });
        return columnDef?.connectionType || 'none';
      }
    }

    return 'none';
  }

  getSelectedFieldsForTable(tableIndex: number): string[] {
    return Array.from(this.selectedFields)
      .filter(key => key.startsWith(`${tableIndex}_`))
      .map(key => key.split('_')[1]);
  }

  getSelectedColumnsData(): Array<{name: string, type: string, source: string}> {
    const selectedColumns: Array<{name: string, type: string, source: string}> = [];

    // Use tableColumnDefinitions as the single source of truth
    this.tableColumnDefinitions.forEach(def => {
      if (def.columnType === 'metric') {
        selectedColumns.push({
          name: def.columnName,
          type: 'Metric',
          source: 'Base'
        });
      } else if (def.columnType === 'field') {
        // Extract field info from typeId for type information
        const typeIdParts = def.typeId.split('_');
        if (typeIdParts.length >= 3) {
          const datasetId = parseInt(typeIdParts[1]);
          const fieldId = parseInt(typeIdParts[2]);
          const fieldData = this.getFieldById(datasetId, fieldId);

          let source = 'Dataset Field';
          if (def.connectionType === 'primary') {
            source = 'Primary';
          } else if (def.connectionType === 'secondary') {
            source = 'Secondary';
          }

          selectedColumns.push({
            name: def.columnName,
            type: fieldData?.dataType || 'Unknown',
            source
          });
        }
      }
    });

    return selectedColumns;
  }

  getMockTableData(): any[] {
    // Return a single empty row to show table structure
    return [{}];
  }

  clearAllSelections() {
    this.selectedFields.clear();
    this.tableColumnDefinitions = [];
  }

  // Metric handling methods
  onMetricDragStart(event: DragEvent, metricIndex: number) {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', `metric_${metricIndex}`);
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onMetricClick(metricIndex: number, metricName: string) {
    const fieldKey = `metric_${metricIndex}_${metricName}`;

    if (this.selectedFields.has(fieldKey)) {
      // Deselect the metric
      this.selectedFields.delete(fieldKey);
      this.removeColumnDefinition(fieldKey);
    } else {
      // Select the metric
      this.selectedFields.add(fieldKey);
      this.addColumnDefinition(metricIndex, metricName, 'metric');
    }
  }

  private addColumnDefinition(tableIndex: number, fieldName: string, columnType: 'metric' | 'field') {
    if (columnType === 'metric') {
      const metricData = this.workspaceMetricsWithPosition[tableIndex];
      if (metricData) {
        // Check if this metric is already in the column definitions
        const existingIndex = this.tableColumnDefinitions.findIndex(def =>
          def.columnType === 'metric' &&
          def.typeId === `metric_${metricData.metric.metricId}`
        );

        if (existingIndex === -1) {
          // Only add if not already present
          const ordinal = this.tableColumnDefinitions.length;
                      const columnDef: TableColumnDef = {
              columnId: `metric_${metricData.metric.metricId}`,
              ordinal,
              columnType: 'metric',
              typeId: `metric_${metricData.metric.metricId}`,
              format: undefined,
              unit: undefined,
              columnName: metricData.metric.metricName || 'Unknown Metric',
              connectionType: 'base' // Metrics are base
            };
          this.tableColumnDefinitions.push(columnDef);
        }
      }
    } else {
      const tableData = this.workspaceTablesWithPosition[tableIndex];
      if (tableData) {
        const columns = this.getDatasetColumns(tableData.dataset);
        const fieldData = columns.find(col => col.fieldName === fieldName);
        if (fieldData) {
          // Check if a field with the same referenceDataId and referenceFieldId already exists
          // This prevents duplicate columns when multiple datasets have fields referencing the same underlying data
          const existingIndex = this.tableColumnDefinitions.findIndex(def => {
            if (def.columnType !== 'field') return false;

            // For fields with references, check if we already have a field with the same reference
            if (fieldData.referenceDataId && fieldData.referenceDataId > 0 &&
                fieldData.referenceFieldId && fieldData.referenceFieldId > 0) {
              // Parse the existing typeId to check references
              const existingTypeId = def.typeId;
              if (existingTypeId.startsWith('field_')) {
                const parts = existingTypeId.split('_');
                if (parts.length >= 3) {
                  const existingDatasetId = parseInt(parts[1]);
                  const existingFieldId = parseInt(parts[2]);
                  // Check if this field has the same reference
                  const existingField = this.getFieldById(existingDatasetId, existingFieldId);
                  if (existingField &&
                      existingField.referenceDataId === fieldData.referenceDataId &&
                      existingField.referenceFieldId === fieldData.referenceFieldId) {
                    return true;
                  }
                }
              }
            }

            // If the current field has no references, check for exact field name match
            return def.typeId === `field_${fieldData.datasetId}_${fieldData.fieldId}`;
          });

          if (existingIndex === -1) {
            // Only add if not already present
            const ordinal = this.tableColumnDefinitions.length;
            const connectionType = this.determineConnectionType(tableIndex, fieldName);

            // Collect all related fields across datasets
            const relatedFields: Array<{datasetId: number, fieldId: number, fieldName: string, referenceDataId?: number, referenceFieldId?: number}> = [];

            this.workspaceTablesWithPosition.forEach((tableData, idx) => {
              const columns = this.getDatasetColumns(tableData.dataset);
              columns.forEach(column => {
                // Check if this field has the same reference as the source field
                if (fieldData.referenceDataId && fieldData.referenceDataId > 0 &&
                    fieldData.referenceFieldId && fieldData.referenceFieldId > 0 &&
                    column.referenceDataId === fieldData.referenceDataId &&
                    column.referenceFieldId === fieldData.referenceFieldId) {
                  relatedFields.push({
                    datasetId: column.datasetId,
                    fieldId: column.fieldId,
                    fieldName: column.fieldName,
                    referenceDataId: column.referenceDataId,
                    referenceFieldId: column.referenceFieldId
                  });
                }
              });
            });

            // If no related fields found, just add the original field
            if (relatedFields.length === 0) {
              relatedFields.push({
                datasetId: fieldData.datasetId,
                fieldId: fieldData.fieldId,
                fieldName: fieldData.fieldName,
                referenceDataId: fieldData.referenceDataId,
                referenceFieldId: fieldData.referenceFieldId
              });
            }

            const columnDef: TableColumnDef = {
              columnId: `field_${fieldData.datasetId}_${fieldData.fieldId}`,
              ordinal,
              columnType: 'field',
              typeId: `field_${fieldData.datasetId}_${fieldData.fieldId}`,
              format: undefined,
              unit: undefined,
              columnName: fieldData.fieldName || 'Unknown Field',
              connectionType,
              relatedFields: relatedFields
            };
            this.tableColumnDefinitions.push(columnDef);
          }
        }
      }
    }
  }

             private removeColumnDefinition(fieldKey: string) {
    // For metric fields, use the metric ID to find the definition
    if (fieldKey.startsWith('metric_')) {
      const parts = fieldKey.split('_');
      const metricIndex = parseInt(parts[1]);
      const metricName = parts.slice(2).join('_');

      // Find the metric data to get the metricId
      const metricData = this.workspaceMetricsWithPosition[metricIndex];
      if (metricData) {
        const index = this.tableColumnDefinitions.findIndex(def =>
          def.columnType === 'metric' &&
          def.typeId === `metric_${metricData.metric.metricId}`
        );

        if (index !== -1) {
          this.tableColumnDefinitions.splice(index, 1);
          // Reorder remaining columns
          this.tableColumnDefinitions.forEach((def, i) => {
            def.ordinal = i;
          });
        }
      }
    } else {
      // For dataset fields, find the definition using the same logic as addColumnDefinition
      const parts = fieldKey.split('_');
      const tableIndex = parseInt(parts[0]);
      const fieldName = parts.slice(1).join('_');

      // Get the field data to check references
      const tableData = this.workspaceTablesWithPosition[tableIndex];
      if (tableData) {
        const columns = this.getDatasetColumns(tableData.dataset);
        const fieldData = columns.find(col => col.fieldName === fieldName);

        if (fieldData) {
          const index = this.tableColumnDefinitions.findIndex(def => {
            if (def.columnType !== 'field') return false;

            // For fields with references, check if we already have a field with the same reference
            if (fieldData.referenceDataId && fieldData.referenceDataId > 0 &&
                fieldData.referenceFieldId && fieldData.referenceFieldId > 0) {
              // Parse the existing typeId to check references
              const existingTypeId = def.typeId;
              if (existingTypeId.startsWith('field_')) {
                const parts = existingTypeId.split('_');
                if (parts.length >= 3) {
                  const existingDatasetId = parseInt(parts[1]);
                  const existingFieldId = parseInt(parts[2]);
                  // Check if this field has the same reference
                  const existingField = this.getFieldById(existingDatasetId, existingFieldId);
                  if (existingField &&
                      existingField.referenceDataId === fieldData.referenceDataId &&
                      existingField.referenceFieldId === fieldData.referenceFieldId) {
                    return true;
                  }
                }
              }
            }

            // If the current field has no references, check for exact field match
            return def.typeId === `field_${fieldData.datasetId}_${fieldData.fieldId}`;
          });

          if (index !== -1) {
            this.tableColumnDefinitions.splice(index, 1);
            // Reorder remaining columns
            this.tableColumnDefinitions.forEach((def, i) => {
              def.ordinal = i;
            });
          }
        }
      }
    }
  }

    private selectFieldFromAllDatasets(tableIndex: number, fieldName: string) {
    const sourceTableData = this.workspaceTablesWithPosition[tableIndex];
    if (!sourceTableData) return;

    const sourceColumns = this.getDatasetColumns(sourceTableData.dataset);
    const sourceField = sourceColumns.find(col => col.fieldName === fieldName);
    if (!sourceField) return;

    // Find all datasets that have fields with the same referenceDataId and referenceFieldId
    const relatedFields: Array<{tableIndex: number, fieldName: string, fieldData: any}> = [];

    this.workspaceTablesWithPosition.forEach((tableData, idx) => {
      const columns = this.getDatasetColumns(tableData.dataset);

      columns.forEach(column => {
        // Check if this field has the same reference as the source field
        // Only consider it related if both have valid references and they match
        if (sourceField.referenceDataId && sourceField.referenceDataId > 0 &&
            sourceField.referenceFieldId && sourceField.referenceFieldId > 0 &&
            column.referenceDataId === sourceField.referenceDataId &&
            column.referenceFieldId === sourceField.referenceFieldId) {
          relatedFields.push({
            tableIndex: idx,
            fieldName: column.fieldName,
            fieldData: column
          });
        }
      });
    });

    // If we found related fields, select all of them in the UI but only add one to column definitions
    if (relatedFields.length > 0) {
      // Select all related fields in the UI
      relatedFields.forEach(field => {
        const fieldKey = `${field.tableIndex}_${field.fieldName}`;
        this.selectedFields.add(fieldKey);
      });

      // Add only the first one to column definitions (to avoid duplicates)
      const firstField = relatedFields[0];
      this.addColumnDefinition(firstField.tableIndex, firstField.fieldName, 'field');
    } else {
      // No related fields found, just select the original field
      const fieldKey = `${tableIndex}_${fieldName}`;
      this.selectedFields.add(fieldKey);
      this.addColumnDefinition(tableIndex, fieldName, 'field');
    }
  }

    private deselectFieldFromAllDatasets(tableIndex: number, fieldName: string) {
    const sourceTableData = this.workspaceTablesWithPosition[tableIndex];
    if (!sourceTableData) return;

    const sourceColumns = this.getDatasetColumns(sourceTableData.dataset);
    const sourceField = sourceColumns.find(col => col.fieldName === fieldName);
    if (!sourceField) return;

    // Find all datasets that have fields with the same referenceDataId and referenceFieldId
    const relatedFields: Array<{tableIndex: number, fieldName: string}> = [];

    this.workspaceTablesWithPosition.forEach((tableData, idx) => {
      const columns = this.getDatasetColumns(tableData.dataset);

      columns.forEach(column => {
        // Check if this field has the same reference as the source field
        // Only consider it related if both have valid references and they match
        if (sourceField.referenceDataId && sourceField.referenceDataId > 0 &&
            sourceField.referenceFieldId && sourceField.referenceFieldId > 0 &&
            column.referenceDataId === sourceField.referenceDataId &&
            column.referenceFieldId === sourceField.referenceFieldId) {
          relatedFields.push({
            tableIndex: idx,
            fieldName: column.fieldName
          });
        }
      });
    });

    // If we found related fields, deselect all of them
    if (relatedFields.length > 0) {
      relatedFields.forEach(field => {
        const fieldKey = `${field.tableIndex}_${field.fieldName}`;
        this.selectedFields.delete(fieldKey);
      });
    } else {
      // No related fields found, just deselect the original field
      const fieldKey = `${tableIndex}_${fieldName}`;
      this.selectedFields.delete(fieldKey);
    }

    // Remove from column definitions (only need to remove once since they're the same field)
    const fieldKey = `${tableIndex}_${fieldName}`;
    this.removeColumnDefinition(fieldKey);
  }

  removeMetricFromWorkspace(metric: MetricDto) {
    // Remove from positioned metrics
    const posIndex = this.workspaceMetricsWithPosition.findIndex(m => m.metric.metricId === metric.metricId);
    if (posIndex !== -1) {
      this.workspaceMetricsWithPosition.splice(posIndex, 1);

      // Remove any connections that reference this metric
      this.tableConnections = this.tableConnections.filter(connection =>
        !connection.fromField.startsWith(`metric_${posIndex}`)
      );

      // Recalculate field positions after metric removal
      this.calculateFieldPositions();
    }
  }

  // Z-index management methods
  private getNextZIndex(): number {
    return ++this.currentZIndex;
  }

     private bringToFront(tableIndex?: number, metricIndex?: number) {
     if (tableIndex !== undefined) {
       this.workspaceTablesWithPosition[tableIndex].zIndex = this.getNextZIndex();
     }
     if (metricIndex !== undefined) {
       this.workspaceMetricsWithPosition[metricIndex].zIndex = this.getNextZIndex();
     }
   }

       // Column reordering methods
    moveColumnLeft(index: number) {
      if (index > 0) {
        this.reorderColumn(index, index - 1);
      }
    }

    moveColumnRight(index: number) {
      if (index < this.tableColumnDefinitions.length - 1) {
        this.reorderColumn(index, index + 1);
      }
    }

    private reorderColumn(fromIndex: number, toIndex: number) {
      // Reorder the tableColumnDefinitions array
      const columnToMove = this.tableColumnDefinitions[fromIndex];
      this.tableColumnDefinitions.splice(fromIndex, 1);
      this.tableColumnDefinitions.splice(toIndex, 0, columnToMove);

      // Update ordinals for all columns
      this.tableColumnDefinitions.forEach((def, index) => {
        def.ordinal = index;
      });
    }

   removeColumnByIndex(index: number) {
     if (index >= 0 && index < this.tableColumnDefinitions.length) {
       const columnDef = this.tableColumnDefinitions[index];

       // Remove from tableColumnDefinitions
       this.tableColumnDefinitions.splice(index, 1);

       // Update ordinals for remaining columns
       this.tableColumnDefinitions.forEach((def, i) => {
         def.ordinal = i;
       });

               // Remove from selectedFields based on the column type
        if (columnDef.columnType === 'metric') {
          // Find and remove the metric from selectedFields
          this.selectedFields.forEach(fieldKey => {
            if (fieldKey.startsWith('metric_')) {
              // Extract metric ID from typeId and check if it matches
              const metricId = columnDef.typeId.replace('metric_', '');
              if (fieldKey.includes(metricId)) {
                this.selectedFields.delete(fieldKey);
              }
            }
          });
        } else {
          // Find and remove the dataset field from selectedFields
          this.selectedFields.forEach(fieldKey => {
            if (!fieldKey.startsWith('metric_')) {
              // Extract field info from typeId
              const typeIdParts = columnDef.typeId.split('_');
              if (typeIdParts.length >= 3) {
                const datasetId = parseInt(typeIdParts[1]);
                const fieldId = parseInt(typeIdParts[2]);
                const fieldData = this.getFieldById(datasetId, fieldId);

                if (fieldData) {
                  const parts = fieldKey.split('_');
                  const tableIndex = parseInt(parts[0]);
                  const fieldName = parts.slice(1).join('_');

                  if (fieldName === fieldData.fieldName) {
                    this.selectedFields.delete(fieldKey);
                  }
                }
              }
            }
          });
        }
     }
   }

   updateColumnName(index: number, newName: string) {
     if (index >= 0 && index < this.tableColumnDefinitions.length) {
       this.tableColumnDefinitions[index].columnName = newName.trim() || this.tableColumnDefinitions[index].columnName;
     }
   }

   private restoreWorkspaceFromTableDefinition() {
     // Clear workspace state
     this.workspaceTables = [];
     this.workspaceTablesWithPosition = [];
     this.workspaceMetricsWithPosition = [];
     this.selectedFields.clear();
     this.tableConnections = [];

     const datasetsToAdd = new Set<number>();

     // Process each column definition
     this.tableColumnDefinitions.forEach(columnDef => {
       if (columnDef.columnType === 'metric') {
         // Add metric to workspace (this automatically adds its related datasets)
         const metricId = parseInt(columnDef.typeId.replace('metric_', ''));
         const metric = this.metrics.find(m => m.metricId === metricId);
         if (metric) {
           this.addMetricToWorkspace(metric);
         }
       } else if (columnDef.columnType === 'field') {
         // Collect dataset IDs from field columns
         const datasetId = parseInt(columnDef.typeId.split('_')[1]);
         if (!isNaN(datasetId)) {
           datasetsToAdd.add(datasetId);
         }

         // Also add datasets from related fields
         columnDef.relatedFields?.forEach(field => datasetsToAdd.add(field.datasetId));
       }
     });

     // Add any remaining datasets that weren't added by metrics
     datasetsToAdd.forEach(datasetId => {
       const dataset = this.datasets.find(d => d.datasetReferenceTable.datasetId === datasetId);
       // Check if dataset exists and isn't already in workspace (check the simple array that addDatasetToWorkspace checks)
       if (dataset && !this.workspaceTables.some(t => t.datasetReferenceTable.datasetId === datasetId)) {
         this.addDatasetToWorkspace(dataset);
       }
     });

     // Restore field selections
     this.restoreFieldSelections();

     // Update UI after DOM renders
     setTimeout(() => {
       this.calculateFieldPositions();
       this.detectConnections();
     }, 200);
   }


   private restoreFieldSelections() {
     this.selectedFields.clear();

     this.tableColumnDefinitions.forEach(columnDef => {
       if (columnDef.columnType === 'metric') {
         // Select metric
         const metricId = parseInt(columnDef.typeId.replace('metric_', ''));
         const metricIndex = this.workspaceMetricsWithPosition.findIndex(m => m.metric.metricId === metricId);
         if (metricIndex !== -1) {
           this.selectedFields.add(`metric_${metricIndex}_${columnDef.columnName}`);
         }
       } else if (columnDef.columnType === 'field') {
         // Select fields - use related fields if available, otherwise parse from typeId
         if (columnDef.relatedFields?.length) {
           // Select all related fields
           columnDef.relatedFields.forEach(relatedField => {
             const tableIndex = this.workspaceTablesWithPosition.findIndex(t =>
               t.dataset.datasetReferenceTable.datasetId === relatedField.datasetId
             );
             if (tableIndex !== -1) {
               this.selectedFields.add(`${tableIndex}_${relatedField.fieldName}`);
             }
           });
         } else {
           // Fallback: find field by parsing typeId
           const [, datasetIdStr, fieldIdStr] = columnDef.typeId.split('_');
           const datasetId = parseInt(datasetIdStr);
           const fieldId = parseInt(fieldIdStr);

           const tableIndex = this.workspaceTablesWithPosition.findIndex(t =>
             t.dataset.datasetReferenceTable.datasetId === datasetId
           );

           if (tableIndex !== -1) {
             const field = this.workspaceTablesWithPosition[tableIndex].dataset.dataTableSchemas
               ?.find(f => f.fieldId === fieldId);
             if (field) {
               this.selectedFields.add(`${tableIndex}_${field.fieldName}`);
             }
           }
         }
       }
     });
   }
}
